import { YStack } from '@/components/ui/layout/layout';
import { getBundledMcpTemplate } from '@/features/integrations/bundled-mcp-templates';
import type { McpPermission } from '@/features/integrations/integrations.types';
import {
  CapabilityRow,
  ListSection,
} from '@/features/local-mcp/connect-checklist';
import { DeviceNoteCallout } from '@/features/local-mcp/device-note-callout';
import { MCP_PERMISSIONS } from '@/features/local-mcp/server-permissions';

import { AppConnectionStatus } from './app-connection-status';
import { DevicePermissionBanner } from './device-permission-banner';

import type { ConfigurationContext } from './configuration.types';

function RequiredPermissionBanner({
  permission,
}: {
  permission: McpPermission;
}) {
  const { label, accessName, useGranted, requestAccess, openSettings } =
    MCP_PERMISSIONS[permission];
  const granted = useGranted();
  if (granted === undefined) return null;

  return (
    <DevicePermissionBanner
      status={granted ? 'authorized' : 'denied'}
      domain={label}
      accessName={accessName}
      onOpenSettings={granted ? openSettings : requestAccess}
    />
  );
}

export function ComputerUseConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const source = context.localSource;
  if (source?.kind !== 'local_mcp') return null;
  const template = getBundledMcpTemplate(source.trustedTemplateId);
  if (!template) return null;
  const capabilities = template.capabilities ?? [];
  const deviceNote = template.deviceNote ?? [];

  return (
    <YStack className="gap-4 p-6">
      {template.requiredPermissions?.map((permission) => (
        <RequiredPermissionBanner key={permission} permission={permission} />
      ))}
      <AppConnectionStatus
        serverSlug={source.serverSlug}
        appName={template.name}
      />
      {capabilities.length > 0 && (
        <ListSection label="Littlebird will be able to">
          {capabilities.map((capability) => (
            <CapabilityRow key={capability} title={capability} />
          ))}
        </ListSection>
      )}
      {deviceNote.length > 0 && <DeviceNoteCallout lines={deviceNote} />}
    </YStack>
  );
}
